import { useEffect, useState } from 'react'
import { getClient } from '@knowsuchagency/django-allauth'
import { useNavigate } from '@tanstack/react-router'
import { type Tokens, saveTokens, clearTokens } from './tokens'

/**
 * Extract JWT tokens from an allauth response and save them.
 * HeadlessJWTAdapter injects access/refresh into the user object.
 */
function extractAndSaveTokens(data: any): Tokens | null {
  const user = data?.data?.user ?? data?.user
  const access = user?.access
  const refresh = user?.refresh
  if (!access || !refresh) return null

  const tokens: Tokens = { access, refresh }
  saveTokens(tokens)
  return tokens
}

/**
 * Log in with username/password. Returns saved tokens.
 * Throws on auth failure.
 */
export async function login(username: string, password: string): Promise<Tokens> {
  const client = getClient()
  const response = await client.login({ username, password })
  const tokens = extractAndSaveTokens(response)
  if (!tokens) {
    throw new Error('No authentication tokens received')
  }
  return tokens
}

/**
 * Check for an existing session and recover JWT tokens from it.
 * Returns tokens if recovered, null otherwise.
 * Capped at 2s to avoid blocking unauthenticated users.
 */
export async function recoverTokensFromSession(): Promise<Tokens | null> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    const client = getClient()
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('timeout')), 2000)
    })
    const data = await Promise.race([client.getAuthenticationStatus(), timeout])
    if (!data.meta.is_authenticated) return null
    return extractAndSaveTokens(data)
  } catch {
    return null
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Log out: clear tokens and end the backend session.
 */
export async function logout(): Promise<void> {
  clearTokens()
  try {
    const client = getClient()
    await client.logout()
  } catch {
    // Session may already be gone — that's fine
  }
}

/**
 * React hook for handling OAuth callback flow.
 * Extracts tokens from the session after an OAuth redirect and navigates on success.
 */
export function useOAuthCallback(onError?: (error: string) => void) {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function handleCallback() {
      try {
        const client = getClient()
        const authStatus = await client.getAuthenticationStatus()

        if (authStatus.meta.is_authenticated && 'user' in authStatus.data) {
          const tokens = extractAndSaveTokens(authStatus)
          if (tokens) {
            navigate({ to: '/organization' })
          } else {
            const msg = 'No authentication tokens received. Please try again.'
            setError(msg)
            onError?.(msg)
          }
        } else {
          const msg = 'Authentication completed but no user session found. Please try again.'
          setError(msg)
          onError?.(msg)
        }
      } catch (err) {
        console.error('Callback error:', err)
        const msg = 'Authentication failed. Please try again.'
        setError(msg)
        onError?.(msg)
      } finally {
        setIsLoading(false)
      }
    }

    handleCallback()
  }, [navigate, onError])

  return { error, isLoading }
}
